import dayjs from 'dayjs';
import { FiltersCollection } from '@libs/store/filters';
import { PaginatedResponse } from '@libs/models/common';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { RecurrenceListItem, ShiftStatus } from '@libs/models/shifts/shift';
import { filterToQueryParams } from '../../utils';
import { request } from '../../request';

const FallbackTimezone = TIMEZONE_FALLBACK;

export async function fetchRotationsList(
  page = 0,
  pageSize = 10,
  filters: FiltersCollection = {},
): Promise<PaginatedResponse<RecurrenceListItem[]>> {
  const applyFilters = filters ? filterToQueryParams(filters) : {};

  const response = await request.get<PaginatedResponse<ShiftsRecurrenceListResponseItem[]>>(
    '/v2/shifts/series-and-rotations/list-view',
    {
      params: {
        pageNumber: page,
        pageSize: pageSize,
        search: '',
        ...applyFilters,
      },
    },
  );

  return {
    ...response.data,
    items: response.data.items.map((item) => ({
      ...item,
      id: item.id,
      externalId: item.externalId,
      startDate: dayjs(item.startDate.dateTime).tz(item.startDate.timeZone || FallbackTimezone),
      endDate: dayjs(item.lastDate.dateTime).tz(item.lastDate.timeZone || FallbackTimezone),
      department: {
        id: item.department.id || '',
        name: item.department.label || '',
      },
      qualifications: item.staffNurseQualificationFilters,
      status: item.status,
      notes: item.note ? [item.note] : [],
      specialty: item.speciality ? { ...item.speciality, name: item.speciality.title } : null,
      timeZone: item.startDate.timeZone || FallbackTimezone,
      recurrence: item.recurrenceType.toLowerCase() as 'single' | 'series' | 'rotation',
      isArchive: item.isArchive,
      assignees: item.staff?.length
        ? item.staff?.map((staff) => ({
            id: staff.id,
            isInternal: staff.isInternal,
            firstName: staff.firstName,
            lastName: staff.lastName,
            avatar: staff.imageUrl || '',
            fullName: [staff.firstName, staff.lastName].filter(Boolean).join(' '),
            assignedByClient: staff.agencyId !== null,
            agency: staff.agencyId
              ? {
                  id: staff.agencyId,
                  name: staff.agencyName || '',
                }
              : null,
          }))
        : [
            ...(item.assignRequest?.staff && item.assignRequest?.status === 'Pending'
              ? [
                  {
                    ...item.assignRequest.staff,
                    avatar: item.assignRequest.staff.imageUrl,
                    fullName: `${item.assignRequest.staff.firstName || ''} ${item.assignRequest.staff.lastName || ''}`,
                    assignedByClient: false,
                    agency: null,
                    orientedList: (item.assignRequest.staff.orientedRecords ?? []).map((o) => ({
                      locationId: o.locationId,
                      value: o.oriented === 'Yes',
                    })),
                  },
                ]
              : []),
          ],
      isShared: item.isShared,
      allowedMultipleAssignees: item.canHasMultipleAssignee,
      canAssign: item.canAssign,
      canUnassign: item.canUnassign,
      totalShiftCount: item.totalShiftCount,
      assignedShiftCount: item.assignedShiftCount,
      tags: Array.from(new Set(item.tags || [])),
    })),
  };
}

type ShiftsRecurrenceListResponseItem = {
  id: string;
  canShare: boolean;
  canAssign: boolean;
  canUnassign: boolean;
  shiftsToShareCount: number;
  agenciesForShare: {
    id: string; //'staffingpartner-00000000000000000000000000';
    logoURL: string;
    name: string;
    tenantId: string;
  }[];
  assignRequest: {
    status: 'Pending' | 'Approved' | 'Declined';
    staff: {
      id: string;
      imageUrl: string;
      firstName: string;
      lastName: string;
      isInternal: true;
      agencyId: string; //'agency-00000000000000000000000000';
      agencyName: string;
      orientedRecords: [
        {
          locationId: string; //'location-00000000000000000000000000';
          oriented: 'Yes' | 'No';
        },
      ];
    };
    declineReason: string;
  };
  startDate: {
    dateTime: string;
    timeZone: string;
  };
  lastDate: {
    dateTime: string;
    timeZone: string;
  };
  chosenQualifications: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  speciality: {
    id: string;
    title: string;
    category: string;
  } | null;
  status: ShiftStatus;
  note: string;
  staff:
    | {
        id: string;
        firstName: string;
        imageUrl: string | null;
        lastName: string;
        isInternal: boolean;
        agencyId: string | null;
        agencyName: string | null;
      }[]
    | null;
  locationId: string;
  staffNurseQualificationFilters: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  isShared: boolean;
  externalId: string;
  canHasMultipleAssignee: boolean | null;
  department: {
    id: string;
    label: string;
  };
  totalShiftCount: number;
  assignedShiftCount: number;
  tags: string[];
  isArchive: boolean;
  recurrenceType: 'Rotation' | 'Series';
};
